import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Icon } from '../Icon';
import { useStore } from '@store/StoreProvider';

export const FaqSection: React.FC = observer(() => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { i18n } = useStore();
  void i18n.language
  void i18n.version

  // Landing FAQ copy: use API only where values match the section; keep literals when keys are missing or product copy diverges.
  const items = [
    {
      q: i18n.t('landing.faq.launch_time_question'),
      a: i18n.t('landing.faq.setup_time_answer'),
    },
    {
      q: 'Сайт и Telegram - это правда один пульт?',
      a: i18n.t('landing.faq.telegram_answer'),
    },
    {
      q: 'Что происходит после 7 дней триала?',
      a: 'Триал включается сразу при регистрации: 7 дней, 3 оператора, 3 канала связи, до 5 регионов на канал, шаблоны 6 общих и 6 личных, расширенная кастомизация. Бренд ChatX в виджете на триале остаётся. Карта не нужна. По окончании приём новых сообщений приостанавливается, а панель и вся история остаются доступны — оплатили тариф, и всё продолжает работать с того же места.',
    },
    {
      q: 'Можно ли отправлять файлы?',
      a: 'Да, с обеих сторон: клиент прикрепляет файл в виджете, оператор — из панели. Разрешены PNG, JPG и PDF, тип и размер проверяются на сервере. От тарифа это не зависит.',
    },
    {
      q: 'Как устроен архив диалогов?',
      a: 'Завершённый диалог уходит в архив. Оператор видит там те диалоги, в которых сам участвовал, — если на одного клиента за разные дни отвечали двое, диалог будет у обоих. Внутри самого диалога история переписки видна целиком всегда.',
    },
    {
      q: i18n.t('landing.faq.payment_methods_question'),
      a: 'Криптовалюта — USDT (TRC-20), TRON, TON, Bitcoin и Litecoin. Карты и переводы — СБП, карты РФ и зарубежные Visa и MasterCard. Оплата идёт по счёту: выбираете тариф, период и число операторов, получаете счёт и оплачиваете удобным способом. Автосписаний нет — деньги без вашего участия не спишутся.',
    },
    {
      q: i18n.t('landing.faq.operators_question'),
      a: 'Сколько угодно. Оператор — это подписка: одна подписка равна одному оператору, и вы берёте столько подписок, сколько людей в команде. Хоть сто. Тариф на это не влияет — он определяет возможности: число каналов, регионов, шаблонов и уровень кастомизации.',
    },
    {
      q: i18n.t('landing.faq.privacy_question'),
      a: 'Переписка передаётся по защищённому соединению, лишние данные не собираются, профили посетителей не строятся. Серверы размещены в Европе, вне российского правового поля. Сроки хранения переписки и журналов опубликованы в политике конфиденциальности.',
    },
  ];

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="faq" className="relative py-14 sm:py-20 overflow-hidden">
      <div className="land-desktop-block absolute inset-0 pointer-events-none">
        <div className="floaty absolute left-[7%] top-28 w-16 h-16 rounded-2xl border border-brand2/20 bg-brand2/[.04]" />
        <div className="floaty slow absolute left-[13%] bottom-24 w-10 h-10 rounded-full border border-cyan-400/20" />
        <div className="floaty rev absolute left-[5%] top-1/2 w-7 h-7 rotate-45 border border-indigo-400/20" />
        <div className="floaty slow absolute right-[8%] top-32 w-20 h-20 rounded-full border border-indigo-400/15" />
        <div className="floaty absolute right-[13%] bottom-28 w-12 h-12 rotate-45 border border-brand2/20 bg-brand/[.04]" />
        <div className="floaty rev absolute right-[6%] top-1/2 grid place-items-center w-12 h-12 rounded-2xl glass border border-white/10 text-brand2">
          <Icon id="ic-message" className="ic ic-sm" />
        </div>
        <div className="floaty absolute left-[9%] bottom-10 grid place-items-center w-11 h-11 rounded-2xl glass border border-white/10 text-brand2">
          <Icon id="ic-zap" className="ic ic-sm" />
        </div>
      </div>
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12">
          <div className="reveal inline-flex items-center gap-2 text-xs font-semibold text-brand2 uppercase tracking-widest mb-4">
            <span className="w-8 h-px bg-brand2" /> {i18n.t('landing.faq.title')}{' '}
            <span className="w-8 h-px bg-brand2" />
          </div>
          <h2 className="reveal text-3xl sm:text-5xl font-extrabold">
            Коротко о <span className="text-gradient">главном</span>
          </h2>
        </div>

        <div data-stagger className="space-y-3">
          {items.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={item.q}
                className={`reveal faq rounded-2xl border overflow-hidden ${
                  isOpen ? 'faq-open border-white/10' : 'border-white/10 bg-panel/40'
                }`}
              >
                <button
                  type="button"
                  className="faq-q w-full flex items-center justify-between gap-4 text-left px-5 py-4 font-semibold"
                  onClick={() => toggle(i)}
                >
                  <span>{item.q}</span>
                  <span
                    className="faq-ic text-brand2 text-xl transition-transform"
                    style={{ transform: isOpen ? 'rotate(180deg)' : undefined }}
                  >
                    {isOpen ? '-' : '+'}
                  </span>
                </button>
                <div
                  className="faq-a overflow-hidden transition-all duration-300"
                  style={{ maxHeight: isOpen ? '800px' : '0px' }}
                >
                  <p className="px-5 pb-5 text-sm text-slate-400">{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});
